/**
 * Formatacao monetaria — ADR-0003.
 *
 * ESTE ARQUIVO SO FORMATA. NAO CALCULA.
 *
 * A API envia valores como STRING (`"1234.5600"`), nunca como numero JSON:
 * numero JSON viraria `double` no JavaScript e reintroduziria o erro de ponto
 * flutuante exatamente na ultima fronteira do sistema.
 *
 * Totais, somas e saldos vem calculados do backend. Se em algum lugar da
 * interface for preciso somar valores, o certo e pedir o total a API — nao
 * somar aqui. `0.1 + 0.2 !== 0.3` em JavaScript, e num sistema financeiro isso
 * vira centavo que ninguem rastreia.
 */

/** Valor monetario como recebido da API: string decimal com 4 casas. */
export type MoneyString = string;

const formatter = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const plainFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * `"1234.5600"` -> `"R$ 1.234,56"`
 *
 * O Number() aqui e seguro porque o resultado so vai para exibicao: nenhum
 * calculo depende dele. O valor original em string permanece intacto.
 */
export function formatMoney(value: MoneyString | null | undefined): string {
  if (value == null || value === '') return formatter.format(0);
  return formatter.format(Number(value));
}

/** Sem o simbolo: `"1.234,56"`. Para colunas de tabela onde R$ ja esta no cabecalho. */
export function formatAmount(value: MoneyString | null | undefined): string {
  if (value == null || value === '') return plainFormatter.format(0);
  return plainFormatter.format(Number(value));
}

/** Sinal do valor, sem converter para numero em nenhum calculo. */
export function moneySign(value: MoneyString | null | undefined): -1 | 0 | 1 {
  if (value == null || value === '') return 0;
  if (value.startsWith('-')) return -1;
  return Number(value) === 0 ? 0 : 1;
}

export function isNegative(value: MoneyString | null | undefined): boolean {
  return moneySign(value) === -1;
}

/** `"1.234,56"` digitado pelo usuario -> `"1234.56"` para enviar a API. */
export function parseInputToApi(input: string): string {
  const cleaned = input
    .replace(/[R$\s ]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  return cleaned === '' ? '0' : cleaned;
}

/**
 * `"350.0000"` da API -> `"350,00"` para PREENCHER um campo de valor.
 *
 * ================================================================
 * NAO USE `maskMoneyInput` DIRETO NUM VALOR DA API
 * ================================================================
 *
 * `maskMoneyInput` recebe DIGITADO: joga fora tudo que nao e digito e trata os
 * dois ultimos como centavos. Passar `"350.0000"` para ela deixa os digitos
 * `3500000`, que viram `35.000,00` — o valor multiplicado por cem.
 *
 * Foi exatamente o defeito do formulario de contrato: abrir um contrato de
 * R$ 350,00 mostrava R$ 35.000,00, e salvar sem mexer gravava o valor errado.
 * Um erro de exibicao que virava erro de dado no clique seguinte.
 *
 * O SINAL E DESCARTADO, como em `maskMoneyInput`. Campo de valor nao aceita
 * `-` digitado; quem precisa de negativo controla o sinal a parte (é o que o
 * cadastro de conta faz com o saldo inicial).
 */
export function moneyToInput(value: MoneyString | null | undefined): string {
  if (value == null || value === '') return '';

  const [intRaw = '', decRaw = ''] = String(value).trim().split('.');

  const inteiro = intRaw.replace(/\D/g, '') || '0';
  const decimais = decRaw.replace(/\D/g, '');

  // Centavos por STRING, nunca `Number(x) * 100`: a multiplicacao em ponto
  // flutuante e o erro que o ADR-0003 existe para evitar.
  let digitos = inteiro + (decimais + '00').slice(0, 2);

  // A API guarda 4 casas (DECIMAL(19,4)) e o campo so tem 2. Truncar perderia
  // meio centavo em silencio; arredonda-se olhando a terceira casa.
  if (decimais.length > 2 && Number(decimais[2]) >= 5) {
    digitos = incrementDigits(digitos);
  }

  return maskMoneyInput(digitos);
}

/** Soma 1 a uma string de digitos, propagando o "vai um". */
function incrementDigits(digits: string): string {
  const out = digits.split('');

  for (let i = out.length - 1; i >= 0; i--) {
    if (out[i] !== '9') {
      out[i] = String(Number(out[i]) + 1);
      return out.join('');
    }
    out[i] = '0';
  }

  return '1' + out.join('');
}

/** Mascara para campo de valor: digita-se 123456 e aparece 1.234,56. */
export function maskMoneyInput(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits === '') return '';

  const cents = digits.padStart(3, '0');
  const intPart = cents.slice(0, -2).replace(/^0+(?=\d)/, '');
  const decPart = cents.slice(-2);

  return `${intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decPart}`;
}

// --- Datas -----------------------------------------------------------

/** `"2026-03-15"` -> `"15/03/2026"`. Sem `new Date()`: ele aplicaria fuso. */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return d && m && y ? `${d}/${m}/${y}` : '';
}

/** `"15/03/2026"` -> `"2026-03-15"`. */
export function parseDateToApi(brDate: string): string {
  const [d, m, y] = brDate.split('/');
  return d && m && y ? `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}` : '';
}
